import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import WaveOverlay from '../components/WaveOverlay';
import globalNetworkImg from '../assets/images/Global_network_generated.jpg';

const Contact = ({ onBookMeeting }) => {
  return (
    <>
      <Helmet>
        <title>Contact Us | Insight Digital Agency</title>
        <meta name="description" content="Get in touch with Insight Digital Agency. Call 913-513-1525, email info@insightdigitalagency.com, or book a meeting. Based in Overland Park, KS, serving clients nationwide." />
        <link rel="canonical" href="https://insightdigitalagency.com/contact" />
        <meta property="og:title" content="Contact Us | Insight Digital Agency" />
        <meta property="og:description" content="Get in touch with Insight Digital Agency. Call 913-513-1525 or book a meeting to discuss your digital marketing needs." />
        <meta property="og:url" content="https://insightdigitalagency.com/contact" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How long does it take to see results from digital marketing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Results vary depending on the service, but typically you can expect to see initial improvements within 30-60 days, with significant results within 3-6 months."
              }
            },
            {
              "@type": "Question",
              "name": "Do you work with businesses outside of real estate and mortgage?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "While we specialize in real estate and mortgage, we also work with other small to medium businesses that can benefit from our digital marketing expertise."
              }
            },
            {
              "@type": "Question",
              "name": "What makes Insight Digital Agency different?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our founder's background in real estate gives us unique insights into your industry. We combine this expertise with proven digital marketing strategies to deliver results that matter."
              }
            },
            {
              "@type": "Question",
              "name": "Do you offer custom packages?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely! We believe every business is unique, so we create customized packages tailored to your specific needs, goals, and budget."
              }
            }
          ]
        })}
        </script>
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection textOnly={true}>
        <div className="max-w-4xl mx-auto text-center space-y-6 relative z-40">
          <div className="flex items-center justify-center space-x-2 text-orange-300">
            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Insight Digital Agency
            </span>
            <span>» Contact Us</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">Contact Us</h1>
          <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
            Get in touch with us today to start your journey toward digital success! Whether you have questions 
            about our services, want to discuss your project in more detail, or are ready to get started, our 
            friendly team is here to help.
          </p>
        </div>
      </HeroSection>

      {/* Contact Form and Info Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h2>
              <div className="w-full">
                <iframe
                  src="https://link.insightdigitalagency.com/widget/form/8sjw9eeFGD9a3VGPbJp4"
                  style={{width:'100%', height:'800px', border:'none', borderRadius:'8px'}}
                  title="Contact Us"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  We're here to help you succeed in the digital world. Reach out to us through any of the 
                  following methods, and we'll get back to you as soon as possible.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Phone</h3>
                    <a 
                      href="tel:913-513-1525" 
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      913-513-1525
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Monday - Friday, 9:00 AM - 6:00 PM CST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Email</h3>
                    <a 
                      href="mailto:info@insightdigitalagency.com" 
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      info@insightdigitalagency.com
                    </a>
                    <p className="text-sm text-gray-600 mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600">
                      Overland Park, KS<br />
                      United States
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Serving clients nationwide</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <div className="text-gray-600 space-y-1">
                      <p>Monday - Friday: 9:00 AM - 6:00 PM CST</p>
                      <p>Saturday: 10:00 AM - 2:00 PM CST</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Quick Response Promise</h3>
                <p className="text-blue-700 text-sm">
                  We understand that time is valuable in business. That's why we commit to responding to all 
                  inquiries within 24 hours during business days. For urgent matters, please call us directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Service Area</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              While we're based in Overland Park, we proudly serve real estate and mortgage professionals 
              across the United States with our digital marketing services.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="relative">
              <img 
                src={globalNetworkImg}
                alt="Global network world map" 
                className="w-full h-96 object-cover" 
              />
              <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm rounded px-3 py-1">
                <div style={{fontSize:'0.8em', color:'white'}}>
                  World map vector courtesy of
                  <a 
                    href="https://www.vecteezy.com/vector-art/2923747-global-network-connection-world-map-point-and-line-composition-concept-of-global-business-vector-illustration" 
                    target="_blank" 
                    rel="noopener" 
                    style={{color:'#60a5fa', textDecoration:'underline', marginLeft:'0.3em'}}>
                    Vecteezy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Here are some common questions we receive. If you don't see your question here, feel free to contact us.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How long does it take to see results from digital marketing?",
                answer: "Results vary depending on the service, but typically you can expect to see initial improvements within 30-60 days, with significant results within 3-6 months."
              },
              {
                question: "Do you work with businesses outside of real estate and mortgage?",
                answer: "While we specialize in real estate and mortgage, we also work with other small to medium businesses that can benefit from our digital marketing expertise."
              },
              {
                question: "What makes Insight Digital Agency different?",
                answer: "Our founder's background in real estate gives us unique insights into your industry. We combine this expertise with proven digital marketing strategies to deliver results that matter."
              },
              {
                question: "Do you offer custom packages?",
                answer: "Absolutely! We believe every business is unique, so we create customized packages tailored to your specific needs, goals, and budget."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Online Presence?</h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Don't wait to start growing your business online. Contact us today for a free consultation 
            and discover how we can help you achieve your digital marketing goals.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg"
          >
            Book a Meeting
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
    </>
  );
};

export default Contact;

